import { type ApiEnvelope, api, unwrap } from "@/lib/api";

// ─── Teams ─────────────────────────────────────────────

export interface TeamSummary {
	id: string;
	name: string;
	description: string | null;
	manager: { id: string; email: string; fullName: string } | null;
	members: {
		id: string;
		email: string;
		fullName: string;
		role?: { key: string; label: string };
	}[];
	memberCount: number;
	createdAt: string;
	updatedAt: string;
}

export interface CreateTeamPayload {
	name: string;
	description?: string;
	managerId?: string;
	memberIds?: string[];
}

export const teamsApi = {
	list: () =>
		unwrap<TeamSummary[]>(api.get<ApiEnvelope<TeamSummary[]>>("/admin/teams")),
	get: (id: string) =>
		unwrap<TeamSummary>(
			api.get<ApiEnvelope<TeamSummary>>(`/admin/teams/${id}`),
		),
	create: (p: CreateTeamPayload) =>
		unwrap<TeamSummary>(api.post<ApiEnvelope<TeamSummary>>("/admin/teams", p)),
	update: (id: string, p: Partial<CreateTeamPayload>) =>
		unwrap<TeamSummary>(
			api.patch<ApiEnvelope<TeamSummary>>(`/admin/teams/${id}`, p),
		),
	remove: (id: string) =>
		unwrap<void>(api.delete<ApiEnvelope<void>>(`/admin/teams/${id}`)),
};

// ─── Audit ─────────────────────────────────────────────

export interface AuditEntry {
	id: string;
	at: string;
	actorId: string | null;
	actorLabel: string;
	actorRole: string | null;
	action: string;
	category: string;
	target: string | null;
	result: "success" | "failure";
	detail: string | null;
	ip: string | null;
	userAgent: string | null;
	prevHash: string | null;
	hash: string;
}

export interface AuditListResponse {
	items: AuditEntry[];
	total: number;
	page: number;
	pageSize: number;
}

export interface AuditQuery {
	category?: string;
	action?: string;
	actorId?: string;
	result?: "success" | "failure";
	from?: string;
	to?: string;
	page?: number;
	pageSize?: number;
}

export const auditApi = {
	list: (q: AuditQuery) => {
		const usp = new URLSearchParams();
		Object.entries(q).forEach(([k, v]) => {
			if (v !== undefined && v !== "") usp.set(k, String(v));
		});
		return unwrap<AuditListResponse>(
			api.get<ApiEnvelope<AuditListResponse>>(`/admin/audit?${usp.toString()}`),
		);
	},
};

// ─── Admin Security ────────────────────────────────────

export interface GlobalSession {
	id: string;
	user: { id: string; email: string; fullName: string };
	userAgent: string | null;
	ip: string | null;
	lastActivityAt: string;
	expiresAt: string;
	createdAt: string;
}

export interface FailedAttempt {
	id: string;
	identifier: string;
	ip: string | null;
	attemptedAt: string;
}

export const adminSecurityApi = {
	sessions: () =>
		unwrap<GlobalSession[]>(
			api.get<ApiEnvelope<GlobalSession[]>>("/admin/security/sessions"),
		),
	revokeSession: (id: string) =>
		unwrap<void>(
			api.delete<ApiEnvelope<void>>(`/admin/security/sessions/${id}`),
		),
	failedAttempts: () =>
		unwrap<FailedAttempt[]>(
			api.get<ApiEnvelope<FailedAttempt[]>>("/admin/security/failed-attempts"),
		),
};

// ─── Approvals ─────────────────────────────────────────

export interface PendingApproval {
	id: string;
	action: string;
	summary: string;
	payload: Record<string, unknown>;
	reason: string | null;
	status: "pending" | "approved" | "rejected" | "expired" | "executed";
	requestedBy: {
		id: string;
		email: string;
		firstName: string;
		lastName: string;
	};
	reviewedBy: {
		id: string;
		email: string;
		firstName: string;
		lastName: string;
	} | null;
	reviewedAt: string | null;
	reviewerNote: string | null;
	expiresAt: string;
	createdAt: string;
}

export interface SubmitApprovalPayload {
	action: string;
	summary: string;
	payload: Record<string, unknown>;
	reason?: string;
}

export const approvalsApi = {
	list: (status?: string) => {
		const qs = status ? `?status=${status}` : "";
		return unwrap<PendingApproval[]>(
			api.get<ApiEnvelope<PendingApproval[]>>(`/admin/approvals${qs}`),
		);
	},
	submit: (p: SubmitApprovalPayload) =>
		unwrap<PendingApproval>(
			api.post<ApiEnvelope<PendingApproval>>("/admin/approvals", p),
		),
	approve: (id: string, note?: string) =>
		unwrap<PendingApproval>(
			api.post<ApiEnvelope<PendingApproval>>(`/admin/approvals/${id}/approve`, {
				note,
			}),
		),
	reject: (id: string, note: string) =>
		unwrap<PendingApproval>(
			api.post<ApiEnvelope<PendingApproval>>(`/admin/approvals/${id}/reject`, {
				note,
			}),
		),
};
